import environment from "@app/config/environment";
import type {
  AgentClientMessage,
  AgentResponseDonePayload,
  AgentServerAction,
  AgentServerMessage,
  BaseAgentServerMessage
} from "@globals";
import { type GenericSchema, any, object, optional, picklist, safeParse } from "valibot";

const agentServerActions: AgentServerAction[] = [
  "authentication",
  "agent.response.chunk",
  "agent.response.done",
  "agent.tool_use",
  "agent.error",
  "agent.ready",
  "agent.pong",
  // Sent when a detached turn (surviving a page refresh) completes, so the client
  // refetches the persisted conversation. Must be listed here or valibot drops it
  // before the useAgentChat handler sees it.
  "agent.history.updated"
];

const agentServerMessageSchema = object({
  action: picklist(agentServerActions),
  payload: optional(any())
}) satisfies GenericSchema<BaseAgentServerMessage>;

/**
 * Response item type for extras - union of all item types
 */
export type ResponseItem = NonNullable<AgentResponseDonePayload["extras"]>[number];

type ConnectionState = "disconnected" | "connecting" | "connected";

type AgentWebSocketListener = (message: AgentServerMessage) => void;
type StateChangeListener = (state: ConnectionState) => void;

/**
 * WebSocket client for agent communication.
 * Uses the /ws/agent/ endpoint for real-time AI agent interactions.
 */
class AgentWebSocketClient {
  private ws: WebSocket | null = null;
  private pingTimer: number | null = null;
  private listeners = new Set<AgentWebSocketListener>();
  private stateListeners = new Set<StateChangeListener>();
  private _state: ConnectionState = "disconnected";
  private getToken: (() => Promise<string>) | null = null;

  get state(): ConnectionState {
    return this._state;
  }

  private setState(state: ConnectionState) {
    this._state = state;
    for (const listener of this.stateListeners) {
      listener(state);
    }
  }

  async connect(getToken?: () => Promise<string>, teamId?: string | null) {
    if (getToken) {
      this.getToken = getToken;
    }

    if (this.ws) {
      this.close();
    }

    this.setState("connecting");

    try {
      if (!this.getToken) {
        throw new Error("AgentWebSocket not initialized: getToken is not set");
      }
      const token = await this.getToken();
      // Scope the WS to the resource's team — a missing team_id makes write ops 403
      // with `request_team_id=None`.
      const teamParam = teamId ? `&team_id=${teamId}` : "";
      const url = `${environment.ATI_API_BASE_URL}/ws/agent/?token=Bearer ${token}${teamParam}`;

      this.ws = new WebSocket(url);
      this.ws.addEventListener("open", this.onOpen);
      this.ws.addEventListener("message", this.onMessage);
      this.ws.addEventListener("close", this.onClose);
      this.ws.addEventListener("error", this.onError);
    } catch {
      this.setState("disconnected");
    }
  }

  close() {
    this.stopPing();
    if (this.ws) {
      this.ws.removeEventListener("open", this.onOpen);
      this.ws.removeEventListener("message", this.onMessage);
      this.ws.removeEventListener("close", this.onClose);
      this.ws.removeEventListener("error", this.onError);
      this.ws.close();
      this.ws = null;
    }
    this.setState("disconnected");
  }

  send(message: AgentClientMessage) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  onServerMessage(listener: AgentWebSocketListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onStateChange(listener: StateChangeListener): () => void {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }

  private onOpen = (event: Event) => {
    // Ignore events from stale WebSocket connections
    if (event.target !== this.ws) {
      return;
    }
    this.startPing();
  };

  private onMessage = (event: MessageEvent) => {
    if (typeof event.data !== "string") {
      return;
    }

    // Ignore messages from stale WebSocket connections
    if (event.target !== this.ws) {
      return;
    }

    try {
      const data = safeParse(agentServerMessageSchema, JSON.parse(event.data));

      if (data.issues) {
        // eslint-disable-next-line no-console
        console.error("❌ Failed to parse WebSocket message");
        return;
      }

      // Cast is safe: valibot validates action is a valid AgentServerAction
      const message = data.output as AgentServerMessage;

      if (message.action === "agent.pong" || message.action === "authentication") {
        return;
      }

      if (message.action === "agent.ready") {
        this.setState("connected");
        return;
      }

      for (const listener of this.listeners) {
        listener(message);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("❌ Failed to parse agent WebSocket message", error);
    }
  };

  private onClose = (event: CloseEvent) => {
    // Ignore events from stale WebSocket connections
    if (event.target !== this.ws) {
      return;
    }

    this.stopPing();
    this.ws = null;
    this.setState("disconnected");
  };

  private onError = (event: Event) => {
    // Ignore events from stale WebSocket connections
    if (event.target !== this.ws) {
      return;
    }

    this.ws?.close();
  };

  private startPing() {
    this.stopPing();
    this.pingTimer = window.setInterval(() => {
      this.send({ action: "agent.ping" });
    }, 15_000);
  }

  private stopPing() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }
}

const agentWebSocket = new AgentWebSocketClient();

export default agentWebSocket;
